import './ScoresExplainer.css';

import React from 'react';
import PropTypes from 'prop-types';

import Team from '@views/Team';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import moment from 'moment';

const getPointsClass = (points) => `points${points === 0 ? '' : ' points-set'}`;

// HACK: for some reason locale(es) is not recognized
const formatDate = (dateStr) =>
  moment(dateStr).format('D MMM').replace('Dec', 'Dic');

const MatchHeader = ({ match, onClick, expanded }) => (
  <div className="match-header pointer" onClick={onClick}>
    <div className="match-date">{formatDate(match.datetime)}</div>
    <div className="score-match">
      <Team code={match.teamHome} />
      <span className="vs">vs</span>
      <Team code={match.teamAway} />
    </div>
    <div className={getPointsClass(match.total)}>
      {match.hasResults ? match.total : '-'}
    </div>
    <div className="caret">
      {match.hasResults ? expanded ? <UpOutlined /> : <DownOutlined /> : null}
    </div>
  </div>
);

const MatchDetailsRow = ({ label, value }) => {
  return (
    <div className="explain-row mi-mundialito">
      <span>{label}</span>
      <span className={getPointsClass(value)}>{value}</span>
      <span className="caret"></span>
    </div>
  );
};

const MatchDetails = ({ match, pointsVersion }) => {
  const { result, exact, extra, full } = match;
  const legacyIdeal = pointsVersion < 2;
  return (
    <div className="match-details">
      {legacyIdeal ? (
        <>
          <MatchDetailsRow label="Resultado (mundial ideal)" value={extra} />
          <MatchDetailsRow label="Resultado exacto (mundial ideal)" value={full} />
        </>
      ) : null}
      <MatchDetailsRow label="Resultado" value={result} />
      <MatchDetailsRow label="Resultado exacto" value={exact} />
    </div>
  );
};

const MatchExplainer = ({ match, pointsVersion }) => {
  const [expanded, setExpanded] = React.useState(false);
  return (
    <div key={match.mid} className={`${expanded ? 'expanded' : ''}`}>
      <MatchHeader
        match={match}
        onClick={match.hasResults ? () => setExpanded(!expanded) : () => {}}
        expanded={expanded}
      />
      {expanded ? (
        <MatchDetails match={match} pointsVersion={pointsVersion} />
      ) : null}
    </div>
  );
};

const MatchesExplainer = ({ matches, title, pointsVersion }) => {
  const list = matches.filter((m) => !!m.teamHome || !!m.teamAway);
  return (
    <div className="matches-explainer">
      <h4>{title}</h4>
      {list.length === 0 ? (
        <p className="empty">Aún no disponible</p>
      ) : (
        list.map((match) => {
          return (
            <MatchExplainer
              match={match}
              key={match.mid}
              pointsVersion={pointsVersion}
            />
          );
        })
      )}
    </div>
  );
};

const GroupsExplainer = ({ groups, pointsVersion }) => {
  return (
    <div className="groups-explainer">
      <div className="title-header">
        <h3>Fase de Grupos</h3>
        <h3>Puntos</h3>
        <span className="caret"></span>
      </div>
      {Object.keys(groups)
        .sort()
        .map((groupName) => {
          return (
            <MatchesExplainer
              matches={groups[groupName]}
              title={`Grupo ${groupName.toUpperCase()}`}
              key={groupName}
              pointsVersion={pointsVersion}
            />
          );
        })}
    </div>
  );
};

const GroupWinner = ({ groupWinner }) => {
  const [expanded, setExpanded] = React.useState(false);
  return (
    <div className={`${expanded ? 'expanded' : ''}`}>
      <div
        className="team-header pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <Team code={groupWinner.team} />
        <div className={getPointsClass(groupWinner.total)}>
          {groupWinner.total}
        </div>
        <div className="caret">
          {expanded ? <UpOutlined /> : <DownOutlined />}
        </div>
      </div>
      {expanded ? (
        <div className="group-winner-details">
          <div className="explain-row mi-mundialito">
            <span>Clasificación </span>
            <span className={getPointsClass(groupWinner.extra)}>
              {groupWinner.extra}
            </span>
            <span className="caret"></span>
          </div>
          <div className="explain-row mi-mundialito">
            <span>Posición </span>
            <span className={getPointsClass(groupWinner.full)}>
              {groupWinner.full}
            </span>
            <span className="caret"></span>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const BracketWinners = ({ bracketWinners, title }) => {
  const list = bracketWinners.filter((w) => !!w.team);
  return (
    <div className="bracket-winners">
      <h4>{title}</h4>
      {list.length === 0 ? (
        <p className="empty">Aún no disponible</p>
      ) : (
        list.map((w) => {
          return (
            <div key={w.team}>
              <div className="team-header">
                <Team code={w.team} />
                <div className={getPointsClass(w.total)}>{w.total}</div>
                <span className="caret"></span>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

const BracketExplainer = ({
  brackets,
  bracketWinners,
  usesWc2026Bracket,
  pointsVersion,
}) => {
  return (
    <div className="bracket-explainer">
      <h3>Fase de Eliminatorias</h3>
      {usesWc2026Bracket ? (
        <MatchesExplainer
          matches={brackets.roundof32 || []}
          title={`Partidos de dieciseisavos`}
          pointsVersion={pointsVersion}
        />
      ) : null}
      <MatchesExplainer
        matches={brackets.roundof16}
        title={`Partidos de octavos`}
        pointsVersion={pointsVersion}
      />
      <BracketWinners
        bracketWinners={bracketWinners.quarter}
        title="Equipos clasificados a cuartos"
      />
      <MatchesExplainer
        matches={brackets.quarter}
        title={`Partidos de cuartos`}
        pointsVersion={pointsVersion}
      />
      <BracketWinners
        bracketWinners={bracketWinners.semi}
        title="Equipos clasificados a semi finales"
      />
      <MatchesExplainer
        matches={brackets.semi}
        title={`Partidos de semi finales`}
        pointsVersion={pointsVersion}
      />
      <BracketWinners
        bracketWinners={bracketWinners.final}
        title="Equipos clasificados a la final"
      />
      <MatchesExplainer
        matches={brackets.third}
        title={`Partido por el tercer puesto`}
        pointsVersion={pointsVersion}
      />
      <MatchesExplainer
        matches={brackets.final}
        title={`Partido final`}
        pointsVersion={pointsVersion}
      />
    </div>
  );
};

const ScoresExplainer = ({
  isReady,
  pointsVersion,
  groups,
  brackets,
  bracketWinners,
  champion,
  total,
  usesWc2026Bracket,
}) => {
  if (!isReady) {
    return <h3>Cargando...</h3>;
  }
  return (
    <div className="scores-explainer">
      <GroupsExplainer groups={groups} pointsVersion={pointsVersion} />
      <BracketExplainer
        brackets={brackets}
        bracketWinners={bracketWinners}
        usesWc2026Bracket={usesWc2026Bracket}
        pointsVersion={pointsVersion}
      />
      <BracketWinners bracketWinners={[champion]} title="Campeón" />
      <h3>
        Total de puntos: <span className="puntos-totales">{total}</span>
      </h3>
    </div>
  );
};

ScoresExplainer.displayName = 'Scores';
ScoresExplainer.propTypes = {
  isReady: PropTypes.bool,
  pointsVersion: PropTypes.number,
  groups: PropTypes.object,
  brackets: PropTypes.object,
  bracketWinners: PropTypes.object,
  champion: PropTypes.object,
  total: PropTypes.number,
  usesWc2026Bracket: PropTypes.bool,
};
ScoresExplainer.defaultProps = {
  isReady: false,
  pointsVersion: 2,
  usesWc2026Bracket: false,
};

export default ScoresExplainer;
